import { inject, Injectable, isDevMode } from "@angular/core";
import {
  ActivatedRouteSnapshot,
  Router,
  RouterStateSnapshot,
  UrlTree,
} from "@angular/router";
import { CookieService } from "ngx-cookie-service";
import { NGXLogger } from "ngx-logger";
import { filter, first, map, Observable, of } from "rxjs";
import { CacheService } from "src/app/core/services/core/cache.service";
import { OnBoardingSection } from "./models/user.model";
import { AlertStore } from "./stores/alert.store";
import { AuthStore } from "./stores/auth.store";
import { DashboardStore } from "./stores/dashboard.store";
import { GatewayStore } from "./stores/gateway.store";
import { MeterStore } from "./stores/meter.store";
import { PlacesStore } from "./stores/places.store";
import { ShowerheadStore } from "./stores/showerhead.store";
import { UserStore } from "./stores/user.store";

const LOG_TAG = "[NAV]";

@Injectable({
  providedIn: "root",
})
export class AuthGuard {
  private _router: Router = inject(Router);
  private _cacheService: CacheService = inject(CacheService);
  private _userStore: UserStore = inject(UserStore);
  private _logger: NGXLogger = inject(NGXLogger);
  private _cookieService: CookieService = inject(CookieService);

  private authStore: AuthStore = inject(AuthStore);
  private meterStore: MeterStore = inject(MeterStore);
  private showerheadStore: ShowerheadStore = inject(ShowerheadStore);
  private placeStore: PlacesStore = inject(PlacesStore);
  private dashboardStore: DashboardStore = inject(DashboardStore);
  private gatewayStore: GatewayStore = inject(GatewayStore);
  private alertStore: AlertStore = inject(AlertStore);

  private get _isAdminModeEnabled(): boolean {
    return this._cookieService.check("admin_on_behalf");
  }

  private _logFlushed = false;

  constructor() {}

  canActivate(
    route: ActivatedRouteSnapshot,
    state: RouterStateSnapshot
  ): Observable<boolean | UrlTree> {
    if (this.authStore.needAuth) {
      // not logged in so redirect to login page with the return url
      console.log("cannot activate => navigate to login");
      // this._cacheService
      //   .clearAll()
      //   .pipe(first())
      //   .subscribe(() => );

      this._router.navigate(["/login"]);

      return of(false);
    } else {
      return this._userStore.userObs.pipe(
        filter((user) => user !== null && user !== undefined),
        first(),
        map((u) => {
          console.log("AUTH-GUARD / user : ", u);

          this._logger.info(LOG_TAG, "navigate to " + state.url);

          if (isDevMode()) {
            this._logger.warn(
              LOG_TAG,
              "development mode, not redirecting for language"
            );
          } else {
            if (
              (window.location.pathname.startsWith("/fr") &&
                u.my_locale !== "fr") ||
              (!window.location.pathname.startsWith("/fr") &&
                u.my_locale == "fr")
            ) {
              this._logger.debug(LOG_TAG, "wrong locale, redirecting");
              window.location.href = `/${u.my_locale}${state.url}`;
            }
          }

          if (state.url.includes("/dashboard")) {
            if (!this.authStore.adminBehalf) {
              if (
                !this._userStore.canViewMeters$() &&
                !this._userStore.canViewSh$()
              ) {
                return this._router.parseUrl("/onboarding");
              }
              if (!this._userStore.user$().timezone) {
                return this._router.parseUrl("/onboarding/timezone");
              }
            }
            this._userStore.startTour(
              OnBoardingSection.General,
              this.dashboardStore.tourSteps$(),
              "/dashboard"
            );
          } else if (state.url.includes("/meters")) {
            this._logger.debug(LOG_TAG, "navigate to meter");
            this._userStore.startTour(
              OnBoardingSection.Meter,
              this.meterStore.tourSteps$(),
              "/meters/devices/view"
            );
          } else if (state.url.includes("/places")) {
            this._logger.debug(LOG_TAG, "navigate to places");
            this._userStore.startTour(
              OnBoardingSection.Places,
              this.placeStore.tourSteps$(),
              "/places"
            );
          } else if (state.url.includes("/alerts")) {
            this._logger.debug(LOG_TAG, "navigate to alerts");
            this._userStore.startTour(
              OnBoardingSection.Alerts,
              this.alertStore.tourSteps$(),
              "/alerts"
            );
          } else if (state.url.startsWith("/showerheads")) {
            this._logger.debug(LOG_TAG, "navigate to showerheads");
            this._userStore.startTour(
              OnBoardingSection.ShowerHead,
              this.showerheadStore.tourSteps$(),
              "/showerheads/devices"
            );
          } else if (state.url.includes("/gateways")) {
            this._logger.debug(LOG_TAG, "navigate to gateways");
            this._userStore.startTour(
              OnBoardingSection.Gateway,
              this.gatewayStore.tourSteps$(),
              "/gateways"
            );
          }
          return true;
        })
      );
    }
  }
}
